import { z } from 'zod';

/* ------------------------------------------------------------------ *
 * Identity
 *
 * Every id is branded. It costs nothing at runtime and it stops the
 * single most common bug in a CRM this wide: passing an OrderId where
 * a ContactId was expected, across 15 modules, and finding out in prod.
 * ------------------------------------------------------------------ */

declare const brand: unique symbol;
export type Branded<T, B extends string> = T & { readonly [brand]: B };

export const idOf = <B extends string>(_brand: B) =>
  z.string().min(1).transform((v) => v as Branded<string, B>);

export type BusinessId = Branded<string, 'Business'>;
export type UserId = Branded<string, 'User'>;
export type PartyId = Branded<string, 'Party'>;
export type ProductId = Branded<string, 'Product'>;
export type OrderId = Branded<string, 'Order'>;
export type OrderLineId = Branded<string, 'OrderLine'>;
export type ShipmentId = Branded<string, 'Shipment'>;
export type InvoiceId = Branded<string, 'Invoice'>;
export type PaymentId = Branded<string, 'Payment'>;
export type LotId = Branded<string, 'Lot'>;
export type ThreadId = Branded<string, 'Thread'>;
export type AppointmentId = Branded<string, 'Appointment'>;
export type PurchaseTaskId = Branded<string, 'PurchaseTask'>;

export const BusinessIdSchema = idOf('Business');
export const UserIdSchema = idOf('User');
export const PartyIdSchema = idOf('Party');
export const ProductIdSchema = idOf('Product');
export const OrderIdSchema = idOf('Order');
export const OrderLineIdSchema = idOf('OrderLine');
export const ShipmentIdSchema = idOf('Shipment');
export const InvoiceIdSchema = idOf('Invoice');
export const PaymentIdSchema = idOf('Payment');
export const LotIdSchema = idOf('Lot');
export const ThreadIdSchema = idOf('Thread');
export const AppointmentIdSchema = idOf('Appointment');
export const PurchaseTaskIdSchema = idOf('PurchaseTask');

/* ------------------------------------------------------------------ *
 * Money
 *
 * Minor units as integers. Never floats — a cent lost per line across
 * a year of invoices is a reconciliation the accountant will lose.
 * Currency travels *with* the amount because a platform tenant in
 * Barcelona and one in Almaty share the same code paths.
 * ------------------------------------------------------------------ */

export const CurrencyCodeSchema = z.string().length(3).toUpperCase();
export type CurrencyCode = z.infer<typeof CurrencyCodeSchema>;

export const MoneySchema = z.object({
  /** Amount in minor units (cents, kopeks). Integer, may be negative. */
  minor: z.number().int(),
  currency: CurrencyCodeSchema,
});
export type Money = z.infer<typeof MoneySchema>;

export const money = (minor: number, currency: CurrencyCode): Money => ({
  minor: Math.round(minor),
  currency,
});

export const zeroMoney = (currency: CurrencyCode): Money => money(0, currency);

const assertSameCurrency = (a: Money, b: Money): void => {
  if (a.currency !== b.currency) {
    throw new Error(`Currency mismatch: ${a.currency} vs ${b.currency}`);
  }
};

export const addMoney = (a: Money, b: Money): Money => {
  assertSameCurrency(a, b);
  return money(a.minor + b.minor, a.currency);
};

export const subMoney = (a: Money, b: Money): Money => {
  assertSameCurrency(a, b);
  return money(a.minor - b.minor, a.currency);
};

export const sumMoney = (items: readonly Money[], currency: CurrencyCode): Money =>
  items.reduce<Money>((acc, m) => addMoney(acc, m), zeroMoney(currency));

/** Multiply by a quantity. Rounds half-away-from-zero, like a till. */
export const mulMoney = (m: Money, factor: number): Money => {
  const raw = m.minor * factor;
  const rounded = raw < 0 ? -Math.round(-raw) : Math.round(raw);
  return money(rounded, m.currency);
};

export const negateMoney = (m: Money): Money => money(-m.minor, m.currency);
export const isZeroMoney = (m: Money): boolean => m.minor === 0;
export const compareMoney = (a: Money, b: Money): number => {
  assertSameCurrency(a, b);
  return a.minor - b.minor;
};

/**
 * Split a total across n shares without losing minor units.
 * Used for spreading a delivery fee over order lines.
 */
export const allocateMoney = (total: Money, weights: readonly number[]): Money[] => {
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  if (totalWeight === 0) return weights.map(() => zeroMoney(total.currency));
  const raw = weights.map((w) => (total.minor * w) / totalWeight);
  const floored = raw.map((r) => Math.floor(r));
  let remainder = total.minor - floored.reduce((a, b) => a + b, 0);
  // Hand the leftover minor units to the largest fractional parts first.
  const order = raw
    .map((r, i) => ({ i, frac: r - Math.floor(r) }))
    .sort((a, b) => b.frac - a.frac);
  const out = [...floored];
  for (const { i } of order) {
    if (remainder <= 0) break;
    out[i] = (out[i] ?? 0) + 1;
    remainder -= 1;
  }
  return out.map((minor) => money(minor, total.currency));
};

/* ------------------------------------------------------------------ *
 * Measures
 *
 * The single most food-specific thing in the domain, and the one that
 * has to stay generic: a business sells by piece, by weight, by volume
 * or by length. Catchweight (ordered by piece, invoiced by actual
 * weight) is the case every generic CRM gets wrong.
 * ------------------------------------------------------------------ */

export const UnitModeSchema = z.enum(['each', 'weight', 'volume', 'length']);
export type UnitMode = z.infer<typeof UnitModeSchema>;

export const UnitSchema = z.enum([
  'pc', 'box', 'crate', 'pallet',      // each
  'g', 'kg', 't', 'lb', 'oz',          // weight
  'ml', 'l', 'hl',                     // volume
  'mm', 'cm', 'm',                     // length
]);
export type Unit = z.infer<typeof UnitSchema>;

export const UNIT_MODE: Record<Unit, UnitMode> = {
  pc: 'each', box: 'each', crate: 'each', pallet: 'each',
  g: 'weight', kg: 'weight', t: 'weight', lb: 'weight', oz: 'weight',
  ml: 'volume', l: 'volume', hl: 'volume',
  mm: 'length', cm: 'length', m: 'length',
};

/** Factor to the mode's base unit (pc, kg, l, m). */
const UNIT_FACTOR: Record<Unit, number> = {
  pc: 1, box: 1, crate: 1, pallet: 1,
  g: 0.001, kg: 1, t: 1000, lb: 0.45359237, oz: 0.028349523125,
  ml: 0.001, l: 1, hl: 100,
  mm: 0.001, cm: 0.01, m: 1,
};

export const QuantitySchema = z.object({
  value: z.number(),
  unit: UnitSchema,
});
export type Quantity = z.infer<typeof QuantitySchema>;

export const qty = (value: number, unit: Unit): Quantity => ({ value, unit });

export const convertQuantity = (q: Quantity, to: Unit): Quantity => {
  if (UNIT_MODE[q.unit] !== UNIT_MODE[to]) {
    throw new Error(`Cannot convert ${q.unit} to ${to}: different measure modes`);
  }
  return qty((q.value * UNIT_FACTOR[q.unit]) / UNIT_FACTOR[to], to);
};

export const addQuantity = (a: Quantity, b: Quantity): Quantity =>
  qty(a.value + convertQuantity(b, a.unit).value, a.unit);

/* ------------------------------------------------------------------ *
 * Handling class
 *
 * Prody colours every row by *what the goods are*, not by workflow
 * state. That is the whole visual system, so it lives in the domain,
 * not in the theme. The list below is the `fresh-food` trait pack;
 * a business on a different pack gets its own classes and the UI
 * follows without a single component change.
 * ------------------------------------------------------------------ */

export const HandlingClassSchema = z.enum([
  'ambient',   // dry goods, shelf-stable
  'chilled',   // 0..4 °C
  'frozen',    // -18 °C
  'fresh',     // highly perishable, same-day
  'live',      // shellfish, live fish
  'hazmat',    // cleaning chemicals, alcohol above threshold
  'neutral',   // non-food, no handling constraint
]);
export type HandlingClass = z.infer<typeof HandlingClassSchema>;

/* ------------------------------------------------------------------ *
 * Temporal
 *
 * Dates that mean "a calendar day in the business's timezone" are
 * stored as plain ISO dates, never as instants. A delivery on the 4th
 * is on the 4th whether the server is in UTC or not.
 * ------------------------------------------------------------------ */

export const PlainDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
export type PlainDate = z.infer<typeof PlainDateSchema>;

export const InstantSchema = z.string().datetime({ offset: true });
export type Instant = z.infer<typeof InstantSchema>;

/** "HH:mm" in the business timezone. */
export const ClockTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:mm');
export type ClockTime = z.infer<typeof ClockTimeSchema>;

export const TimeWindowSchema = z
  .object({ from: ClockTimeSchema, to: ClockTimeSchema })
  .refine((w) => w.from < w.to, { message: 'Window must end after it starts' });
export type TimeWindow = z.infer<typeof TimeWindowSchema>;

export const WeekdaySchema = z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);
export type Weekday = z.infer<typeof WeekdaySchema>;

/* ------------------------------------------------------------------ *
 * Shared record shape
 * ------------------------------------------------------------------ */

export const AuditSchema = z.object({
  createdAt: InstantSchema,
  createdBy: UserIdSchema.nullable(),
  updatedAt: InstantSchema,
  updatedBy: UserIdSchema.nullable(),
  /** Soft delete. Nothing is ever hard-deleted in a system that invoices. */
  deletedAt: InstantSchema.nullable().default(null),
});
export type Audit = z.infer<typeof AuditSchema>;

/**
 * Every top-level record carries its tenant and an open `custom` bag.
 * The bag is what lets a business add "allergen sheet ref" without a
 * migration, and it is why the API can stay stable while tenants differ.
 */
export const TenantScopedSchema = z.object({
  businessId: BusinessIdSchema,
  custom: z.record(z.string(), z.unknown()).default({}),
});

/* ------------------------------------------------------------------ *
 * Paging
 * ------------------------------------------------------------------ */

export const PageSchema = z.object({
  items: z.array(z.unknown()),
  total: z.number().int().nonnegative(),
  offset: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
});

export interface Page<T> {
  items: T[];
  total: number;
  offset: number;
  limit: number;
}

export const emptyPage = <T>(limit = 50): Page<T> => ({
  items: [],
  total: 0,
  offset: 0,
  limit,
});
